import { useEffect } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useAuth } from "./stores/auth";
import { Layout } from "./components/Layout";
import { RequireModule } from "./components/RequireModule";
import { PageLoader } from "./components/ui";
import { SlabWall } from "./components/fx";
import { Home } from "./pages/Home";
import { Shop } from "./pages/Shop";
import { ProductDetail } from "./pages/ProductDetail";
import { Packs } from "./pages/Packs";
import { PackDetail } from "./pages/PackDetail";
import { Raffles } from "./pages/Raffles";
import { RaffleRoom } from "./pages/RaffleRoom";
import { Auctions } from "./pages/Auctions";
import { AuctionRoom } from "./pages/AuctionRoom";
import { Cart } from "./pages/Cart";
import { Checkout } from "./pages/Checkout";
import { CheckoutOrder } from "./pages/CheckoutOrder";
import { CheckoutResult } from "./pages/CheckoutResult";
import { Orders } from "./pages/Orders";
import { OrderDetail } from "./pages/OrderDetail";
import { Track } from "./pages/Track";
import { Wallet } from "./pages/Wallet";
import { Inventory } from "./pages/Inventory";
import { Notifications } from "./pages/Notifications";
import { Support } from "./pages/Support";
import { SupportCaseChat } from "./pages/SupportCaseChat";
import { Account } from "./pages/Account";
import { AccountAddresses } from "./pages/AccountAddresses";
import { AccountSettings } from "./pages/AccountSettings";
import { Wishlist } from "./pages/Wishlist";
import { Login } from "./pages/auth/Login";
import { AuthCallback } from "./pages/auth/AuthCallback";
import { Register } from "./pages/auth/Register";
import { VerifyEmail } from "./pages/auth/VerifyEmail";
import { ForgotPassword } from "./pages/auth/ForgotPassword";
import { ResetPassword } from "./pages/auth/ResetPassword";
import { LegalPage } from "./pages/LegalPage";
import { CmsPage } from "./pages/CmsPage";
import { Leaderboard } from "./pages/Leaderboard";
import { VerifyPhone } from "./pages/account/VerifyPhone";
import { RequirePhoneVerified } from "./components/RequirePhoneVerified";
import { Market } from "./pages/Market";
import { MarketDetail } from "./pages/MarketDetail";
import { RequireMarketplace } from "./components/RequireMarketplace";
import { MarketShop } from "./pages/MarketShop";
import { FlexxiVerify } from "./pages/account/FlexxiVerify";
import { FlexxiPlans } from "./pages/account/FlexxiPlans";
import { FlexxiPlanDetail } from "./pages/account/FlexxiPlanDetail";
import { FlexxiKycCapture } from "./pages/FlexxiKycCapture";

/** Branded cold-start screen — three cards fanning out behind the mark. */
function BootSplash() {
  return (
    <div className="bg-noise relative flex min-h-screen flex-col items-center justify-center overflow-hidden bg-mesh-deep">
      <SlabWall count={6} dim={0.85} size={104} />
      <div className="relative h-32 w-32">
        {[-1, 0, 1].map((offset, i) => (
          <img
            key={offset}
            src="/art/card-back.svg"
            alt=""
            aria-hidden
            className="absolute left-1/2 top-1/2 h-24 w-auto -translate-x-1/2 -translate-y-1/2 opacity-70"
            style={{
              transform: `translate(-50%, -50%) rotate(${offset * 14}deg) translateY(${Math.abs(offset) * 4}px)`,
              animation: `float-soft ${3.2 + i * 0.4}s ease-in-out ${i * 0.2}s infinite`,
            }}
          />
        ))}
        <img
          src="/logo-mark.png"
          alt="Mr Gaole"
          className="breathe absolute left-1/2 top-1/2 h-12 w-12 -translate-x-1/2 -translate-y-1/2 object-contain drop-shadow-[0_0_18px_rgba(221,84,235,0.7)]"
        />
      </div>
    </div>
  );
}

function RequireAuth({ children }: { children: JSX.Element }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <PageLoader />;
  if (!user) return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  return children;
}

export function App() {
  const bootstrap = useAuth((s) => s.bootstrap);
  const loading = useAuth((s) => s.loading);

  useEffect(() => {
    void bootstrap();
  }, [bootstrap]);

  if (loading) return <BootSplash />;

  return (
    <RequirePhoneVerified>
      <Routes>
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route element={<Layout />}>
        <Route path="/" element={<Home />} />
        <Route path="/market" element={<RequireMarketplace><Market /></RequireMarketplace>} />
        <Route path="/market/shop/:shopSlug" element={<RequireMarketplace><MarketShop /></RequireMarketplace>} />
        <Route path="/market/:slug" element={<RequireMarketplace><MarketDetail /></RequireMarketplace>} />
        <Route path="/market/cart" element={<Navigate to="/cart" replace />} />
        <Route path="/market/checkout" element={<Navigate to="/checkout" replace />} />
        <Route path="/shop" element={<RequireModule module="ecommerce"><Shop /></RequireModule>} />
        <Route path="/shop/:slug" element={<RequireModule module="ecommerce"><ProductDetail /></RequireModule>} />
        <Route path="/packs" element={<RequireModule module="packs"><Packs /></RequireModule>} />
        <Route path="/packs/:id" element={<RequireModule module="packs"><PackDetail /></RequireModule>} />
        <Route path="/raffles" element={<RequireModule module="raffles"><Raffles /></RequireModule>} />
        <Route path="/raffles/:id" element={<RequireModule module="raffles"><RaffleRoom /></RequireModule>} />
        <Route path="/auctions" element={<RequireModule module="auctions"><Auctions /></RequireModule>} />
        <Route path="/auctions/:id" element={<RequireModule module="auctions"><AuctionRoom /></RequireModule>} />
        <Route path="/leaderboard" element={<RequireModule module="packs"><Leaderboard /></RequireModule>} />
        <Route path="/flexxi-kyc/capture/:token" element={<FlexxiKycCapture />} />
        <Route path="/track" element={<Track />} />
        <Route path="/track/:orderNo" element={<Track />} />
        <Route path="/legal/:slug" element={<LegalPage />} />
        <Route path="/pages/:slug" element={<CmsPage />} />
        <Route path="/cart" element={<RequireAuth><Cart /></RequireAuth>} />
        <Route path="/checkout" element={<RequireAuth><Checkout /></RequireAuth>} />
        <Route path="/checkout/order/:id" element={<RequireAuth><RequireModule module="ecommerce"><CheckoutOrder /></RequireModule></RequireAuth>} />
        <Route path="/checkout/result" element={<RequireAuth><CheckoutResult /></RequireAuth>} />
        <Route path="/orders" element={<RequireAuth><Orders /></RequireAuth>} />
        <Route path="/orders/:id" element={<RequireAuth><OrderDetail /></RequireAuth>} />
        <Route path="/wallet" element={<RequireAuth><RequireModule module="credits"><Wallet /></RequireModule></RequireAuth>} />
        <Route path="/inventory" element={<RequireAuth><RequireModule module="collection"><Inventory /></RequireModule></RequireAuth>} />
        <Route path="/notifications" element={<RequireAuth><Notifications /></RequireAuth>} />
        <Route
          path="/support"
          element={
            <RequireAuth>
              <RequireModule module="support_forms">
                <Support />
              </RequireModule>
            </RequireAuth>
          }
        />
        <Route
          path="/support/cases/:id"
          element={
            <RequireAuth>
              <RequireModule module="support_forms">
                <SupportCaseChat />
              </RequireModule>
            </RequireAuth>
          }
        />
        <Route path="/account/verify-phone" element={<VerifyPhone />} />
        <Route path="/account" element={<RequireAuth><Account /></RequireAuth>} />
        <Route path="/account/wishlist" element={<RequireAuth><Wishlist /></RequireAuth>} />
        <Route path="/account/addresses" element={<RequireAuth><AccountAddresses /></RequireAuth>} />
        <Route path="/account/settings" element={<RequireAuth><AccountSettings /></RequireAuth>} />
        <Route
          path="/account/flexxi/verify"
          element={
            <RequireAuth>
              <RequireModule module="instalments">
                <FlexxiVerify />
              </RequireModule>
            </RequireAuth>
          }
        />
        <Route
          path="/account/flexxi"
          element={
            <RequireAuth>
              <RequireModule module="instalments">
                <FlexxiPlans />
              </RequireModule>
            </RequireAuth>
          }
        />
        <Route
          path="/account/flexxi/:id"
          element={
            <RequireAuth>
              <RequireModule module="instalments">
                <FlexxiPlanDetail />
              </RequireModule>
            </RequireAuth>
          }
        />
        <Route path="/account/security" element={<Navigate to="/account/settings" replace />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
    </RequirePhoneVerified>
  );
}
